import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from 'FrontRoyalAngular';
import { selectInGhostMode } from 'Users/ghostModeSlice';
import { userActionsApi } from 'Users/UserActionsApi';
import { useErrorLogService } from 'ErrorLogging';
import { useTargetBrandConfig } from 'FrontRoyalAngular/useTargetBrandConfig';
import isNonRelayEmail from 'EmailInput/isNonRelayEmail';
import { Brand } from 'AppBranding';
import filtersQuanticSrc from 'images/welcome-to-network/filters-quantic.jpg';
import filtersValarSrc from 'images/welcome-to-network/filters-valar.jpg';
import mapQuanticSrc from 'images/welcome-to-network/map-quantic.jpg';
import mapValarSrc from 'images/welcome-to-network/map-valar.jpg';
import profilesSrc from 'images/welcome-to-network/profiles.jpg';
import eventsQuanticSrc from 'images/welcome-to-network/events-quantic.jpg';
import eventsValarSrc from 'images/welcome-to-network/events-valar.jpg';
import '../locales/studentNetworkV2/studentNetworkV2-en.json';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type StepConfig =
    | { mode: 'intro'; image: string; titleKey: string; messageKey: string }
    | { mode: 'activation' }
    | { mode: 'email' };

type OnboardingWizardModalProps = {
    onClose: () => void;
};

type OnboardingWizardModalPresentationProps = {
    brandName: string;
    step: StepConfig;
    steps: StepConfig[];
    currentStep: number;
    isLastStep: boolean;
    privacyLabel: string;
    emailValue: string;
    emailError: boolean;
    onNext: () => void;
    onActivate: (activate: boolean) => void;
    onEmailChange: (value: string) => void;
    onSubmitEmail: () => void;
};

// Prefetch all intro images so they display instantly on step transitions
function usePrefetchImages(steps: StepConfig[]) {
    useEffect(() => {
        steps.forEach(s => {
            if (s.mode === 'intro') {
                const img = new Image();
                img.src = s.image;
            }
        });
    }, [steps]);
}

// oxlint-disable-next-line max-lines-per-function -- multi-step wizard presentation
export function OnboardingWizardModalPresentation({
    brandName,
    step,
    steps,
    currentStep,
    isLastStep,
    privacyLabel,
    emailValue,
    emailError,
    onNext,
    onActivate,
    onEmailChange,
    onSubmitEmail,
}: OnboardingWizardModalPresentationProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.onboardingWizard' });
    const introSteps = steps.filter(s => s.mode === 'intro');
    usePrefetchImages(steps);

    return (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/40">
            <div className="mx-4 flex w-full max-w-[480px] flex-col items-center rounded-8 bg-white p-7.5 shadow-[0_4px_20px_0_rgba(0,0,0,0.2)]">
                {/* Intro steps */}
                {step.mode === 'intro' && (
                    <>
                        <img
                            key={step.image}
                            src={step.image}
                            alt=""
                            className="mb-6 aspect-[16/9] w-full rounded-5 object-cover opacity-0 transition-opacity duration-300"
                            onLoad={e => {
                                (e.target as HTMLImageElement).classList.remove('opacity-0');
                            }}
                        />
                        <h2 className="mb-2 text-center text-xl font-semibold text-black">
                            {t(step.titleKey, { brandName })}
                        </h2>
                        <p className="mb-6 text-center text-base text-slate-grey">
                            {t(step.messageKey, { brandName })}
                        </p>
                        {/* Dot indicators */}
                        <div className="mb-6 flex gap-2">
                            {introSteps.map((s, i) => (
                                <div
                                    key={s.mode === 'intro' ? s.titleKey : s.mode}
                                    className={`h-2 w-2 rounded-full ${
                                        i === currentStep ? 'bg-black' : 'bg-slate-grey-200'
                                    }`}
                                />
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={onNext}
                            className="h-12 w-full rounded-5 bg-primary text-base font-semibold text-white transition-colors hover:bg-primary/90"
                        >
                            {isLastStep ? t('getStarted') : t('next')}
                        </button>
                    </>
                )}

                {/* Activation step */}
                {step.mode === 'activation' && (
                    <>
                        <h2 className="mb-2 text-center text-xl font-semibold text-black">{t('activationTitle')}</h2>
                        <p className="mb-6 text-center text-base text-slate-grey">
                            {t('activationMessageBefore')}
                            <span className="font-semibold">{privacyLabel}</span>
                            {t('activationMessageAfter')}
                        </p>
                        <button
                            type="button"
                            onClick={() => onActivate(true)}
                            className="mb-3 h-12 w-full rounded-5 bg-primary text-base font-semibold text-white transition-colors hover:bg-primary/90"
                        >
                            {t('activateMyProfile')}
                        </button>
                        <button
                            type="button"
                            onClick={() => onActivate(false)}
                            className="mb-4 text-base text-slate-grey transition-colors hover:text-black"
                        >
                            {t('staySetting', { setting: privacyLabel })}
                        </button>
                        <p className="text-center text-sm text-slate-grey">{t('changeSettingSmallprint')}</p>
                    </>
                )}

                {/* Email step */}
                {step.mode === 'email' && (
                    <>
                        <h2 className="mb-2 text-center text-xl font-semibold text-black">{t('emailTitle')}</h2>
                        <p className="mb-6 text-center text-base text-slate-grey">{t('emailMessage')}</p>
                        <input
                            type="email"
                            value={emailValue}
                            onChange={e => onEmailChange(e.target.value)}
                            placeholder={t('emailAddress')}
                            className={`mb-1 h-12 w-full rounded-5 border px-4 text-base text-black outline-none placeholder:text-slate-grey ${
                                emailError ? 'border-red-500' : 'border-slate-grey-200 focus:border-black'
                            }`}
                        />
                        {emailError && (
                            <p className="mb-3 text-sm text-red-500">Please enter a valid, non-relay email address.</p>
                        )}
                        <button
                            type="button"
                            onClick={onSubmitEmail}
                            disabled={!emailValue.trim()}
                            className="mt-3 h-12 w-full rounded-5 bg-primary text-base font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
                        >
                            {t('completeSetup')}
                        </button>
                        <p className="mt-4 text-center text-sm text-slate-grey">{t('changeSettingSmallprint')}</p>
                    </>
                )}
            </div>
        </div>
    );
}

// oxlint-disable-next-line max-lines-per-function -- multi-step wizard with intro/activation/email states
export function OnboardingWizardModal({ onClose }: OnboardingWizardModalProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.onboardingWizard' });
    const currentUser = useCurrentUser();
    const inGhostMode = useSelector(selectInGhostMode);
    const [updateCurrentUser] = userActionsApi.useUpdateCurrentUserMutation();
    const brandConfig = useTargetBrandConfig();
    const brandName = brandConfig.brandNameShort;
    const isValar = brandConfig.branding === Brand.valar;
    const errorLogService = useErrorLogService();

    const [currentStep, setCurrentStep] = useState(0);
    const [emailValue, setEmailValue] = useState('');
    const [emailError, setEmailError] = useState(false);
    const [activatedNeedsEmail, setActivatedNeedsEmail] = useState(false);
    const [privacyOverride, setPrivacyOverride] = useState<string | null>(null);

    // Refs mirror the state values so that `complete()` always reads the latest
    // values even when called from a stale closure (e.g., handleActivate → goNext → complete
    // within the same render, before React processes the batched state updates).
    const privacyOverrideRef = useRef<string | null>(null);
    const emailOverrideRef = useRef<string | null>(null);

    const hasEventsAccess = currentUser?.hasStudentNetworkEventsAccess ?? false;
    const hasSeenNetwork = currentUser?.hasSeenStudentNetwork ?? false;
    const privacy = privacyOverride ?? currentUser?.prefStudentNetworkPrivacy;

    // Note: despite the name, isNonRelayEmail() returns true when the email IS
    // a relay/private-relay address. It's a v1 naming quirk we inherited.
    // Here it means: "user's primary email is a relay → they need a real one."
    const missingRequiredEmail = useMemo(() => {
        if (!currentUser) return false;
        return !!(
            privacy === 'full' &&
            !currentUser.studentNetworkEmail &&
            isNonRelayEmail(currentUser.email ?? '') &&
            currentUser.hasAccessToFullStudentNetworkProfiles
        );
    }, [currentUser, privacy]);

    const steps = useMemo(() => {
        const result: StepConfig[] = [];

        if (hasSeenNetwork) {
            if (missingRequiredEmail) result.push({ mode: 'email' });
            return result;
        }

        result.push({
            mode: 'intro',
            image: isValar ? filtersValarSrc : filtersQuanticSrc,
            titleKey: 'stepOneTitle',
            messageKey: 'stepOneMessage',
        });
        result.push({
            mode: 'intro',
            image: isValar ? mapValarSrc : mapQuanticSrc,
            titleKey: 'stepTwoTitle',
            messageKey: 'stepTwoMessage',
        });
        result.push({
            mode: 'intro',
            image: profilesSrc,
            titleKey: 'stepThreeTitle',
            messageKey: 'stepThreeMessage',
        });

        if (hasEventsAccess) {
            result.push({
                mode: 'intro',
                image: isValar ? eventsValarSrc : eventsQuanticSrc,
                titleKey: 'stepFourTitle',
                messageKey: 'stepFourMessage',
            });
        }

        if (privacy && privacy !== 'full') {
            result.push({ mode: 'activation' });
        }

        if (missingRequiredEmail || activatedNeedsEmail) {
            result.push({ mode: 'email' });
        }

        return result;
    }, [hasSeenNetwork, hasEventsAccess, isValar, privacy, missingRequiredEmail, activatedNeedsEmail]);

    const step = steps[currentStep];
    const isLastStep = currentStep >= steps.length - 1;

    const complete = useCallback(() => {
        if (!currentUser) {
            onClose();
            return;
        }

        if (!inGhostMode) {
            const userUpdates: { id: string } & Record<string, unknown> = {
                id: currentUser.id,
                has_seen_student_network: true,
            };
            if (privacyOverrideRef.current) userUpdates.pref_student_network_privacy = privacyOverrideRef.current;
            if (emailOverrideRef.current) userUpdates.student_network_email = emailOverrideRef.current;
            updateCurrentUser({ user: userUpdates }).catch(err => {
                errorLogService?.notify('Onboarding wizard user save failed', null, { error: err });
            });
        }

        onClose();
    }, [currentUser, inGhostMode, onClose, errorLogService, updateCurrentUser]);

    const goNext = useCallback(() => {
        if (isLastStep) {
            complete();
        } else {
            setCurrentStep(prev => prev + 1);
        }
    }, [isLastStep, complete]);

    const handleActivate = useCallback(
        (activate: boolean) => {
            const needsEmail =
                activate &&
                currentUser &&
                !inGhostMode &&
                currentUser.careerProfile &&
                !currentUser.studentNetworkEmail &&
                isNonRelayEmail(currentUser.email ?? '');

            if (activate && currentUser && !inGhostMode && currentUser.careerProfile) {
                privacyOverrideRef.current = 'full';
                setPrivacyOverride('full');
            }

            if (needsEmail) {
                // Append email step via state and advance to it — cannot use goNext
                // because the current isLastStep was computed before the email step existed.
                setActivatedNeedsEmail(true);
                setCurrentStep(prev => prev + 1);
            } else {
                goNext();
            }
        },
        [currentUser, inGhostMode, goNext],
    );

    const handleUpdateEmail = useCallback(() => {
        const trimmed = emailValue.trim();
        if (!trimmed || !EMAIL_REGEX.test(trimmed) || isNonRelayEmail(trimmed)) {
            setEmailError(true);
            return;
        }
        emailOverrideRef.current = trimmed;
        goNext();
    }, [emailValue, goNext]);

    if (!step || steps.length === 0) return null;

    const privacyLabel = privacy === 'hidden' ? t('hidden') : t('anonymous');

    return (
        <OnboardingWizardModalPresentation
            brandName={brandName}
            step={step}
            steps={steps}
            currentStep={currentStep}
            isLastStep={isLastStep}
            privacyLabel={privacyLabel}
            emailValue={emailValue}
            emailError={emailError}
            onNext={goNext}
            onActivate={handleActivate}
            onEmailChange={value => {
                setEmailValue(value);
                setEmailError(false);
            }}
            onSubmitEmail={handleUpdateEmail}
        />
    );
}
